import React, { useState, useEffect, useRef, useMemo } from "react";
import Decimal from "decimal.js";
import { Container, Row, Col, Card, CardBody, Button, Form, Spinner } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { createInvoice } from "../../slices/invoicing/thunk";
import { getDescendants, getCatalogs } from "../../slices/catalogs/thunk";
import { addCustomer, getCustomers, updateCustomer } from "../../slices/customers/thunk";
import { getEmissionPoints } from "../../slices/companies/thunk";
import { toast } from "react-toastify";
import { InvoiceDetail, PaymentMethod, AdditionalField } from "./types";
import IssuerInfo from "./Components/IssuerInfo";
import CustomerInfo from "./Components/CustomerInfo";
import InvoiceDetails from "./Components/InvoiceDetails";
import PaymentMethods from "./Components/PaymentMethods";
import TotalsSummary from "./Components/TotalsSummary";
import FullScreenLoader from "../../Components/Common/FullScreenLoader";
import BillingHeader from "./Components/BillingHeader";
import AdditionalFields from "./Components/AdditionalFields";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import GenericSuccessModal, { createBillingSuccessConfig } from "../../Components/Common/SuccessModal";
import ConfigValidationModal from "./Components/ConfigValidationModal";
import ConfirmModal, { createBillingConfirmData } from "./Components/ConfirmModal";
import { useNavigate } from "react-router-dom";
import { useBlockSpecialChars } from "../../Components/Common/useBlockSpecialChars";

const Billing = () => {
  useBlockSpecialChars();
  document.title = "Facturar | Vuelo";

  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const companiesState = useSelector((state: any) => state.Companies || {});
  const catalogsState = useSelector((state: any) => state.Catalogs || {});
  const customersState = useSelector((state: any) => state.Customers || {});

  const companies = useMemo(() => companiesState.companyList || [], [companiesState.companyList]);
  const establishments = useMemo(() => companiesState.establishmentList || [], [companiesState.establishmentList]);
  const emissionPoints = useMemo(() => companiesState.emissionPointList || [], [companiesState.emissionPointList]);
  const catalogList = useMemo(() => catalogsState.catalogList || [], [catalogsState.catalogList]);
  const descendants = useMemo(() => catalogsState.descendants || {}, [catalogsState.descendants]);
  const customerList = useMemo(() => customersState.customerLists || [], [customersState.customerLists]);

  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [selectedEstablishment, setSelectedEstablishment] = useState<any>(null);
  const [selectedEmissionPoint, setSelectedEmissionPoint] = useState<any>(null);
  const [details, setDetails] = useState<InvoiceDetail[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [additionalFields, setAdditionalFields] = useState<AdditionalField[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  // Estado para controlar la copia de factura y ocultar el formulario hasta terminar
  const [loadingCopy, setLoadingCopy] = useState<boolean>(() => {
    try {
      return !!sessionStorage.getItem('copiedInvoiceData');
    } catch {
      return false;
    }
  });
  const [showDetailsErrors, setShowDetailsErrors] = useState(false);

  // Obtener información del usuario y token
  const authUser = localStorage.getItem("authUser");
  const userToken = authUser ? JSON.parse(authUser).token : null;
  const decodedToken = userToken ? (() => {
    try {
      const base64Url = userToken.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (e) {
      return null;
    }
  })() : null;
  const userRoles = decodedToken?.roles?.map((role: any) => role.name) || [];
  const isSuperAdmin = userRoles.includes('Super Admin');
  const isAdmin = isSuperAdmin || userRoles.includes('Admin');

  // Validación de configuración - simple y directa
  useEffect(() => {
    // Validación especial para usuarios sin configuración en token
    const userEstablishmentId = decodedToken?.establishmentId;
    const userEmissionPointId = decodedToken?.emissionPointId;

    // Si no hay IDs en token, mostrar modal inmediatamente
    if (!userEstablishmentId || !userEmissionPointId) {
      setShowConfigModal(true);
      return;
    }

    // Solo validar cuando los datos estén completamente cargados (para usuarios con token)
    if (!companiesState.isLoading && selectedCompany && establishments.length > 0 && emissionPoints.length > 0) {
      // Verificar si los elementos del usuario existen
      const hasUserEstablishment = establishments.some((e: any) => e.id === userEstablishmentId);
      const hasUserEmissionPoint = emissionPoints.some((ep: any) => ep.id === userEmissionPointId);

      // Solo mostrar modal si falta alguno
      setShowConfigModal(!hasUserEstablishment || !hasUserEmissionPoint);
    }
  }, [companiesState.isLoading, selectedCompany, establishments, emissionPoints, decodedToken]);

  // Cargar puntos de emisión cuando se selecciona establecimiento
  useEffect(() => {
    if (selectedEstablishment) {
      dispatch(getEmissionPoints(selectedEstablishment.id));
    }
  }, [dispatch, selectedEstablishment]);

  // Funciones para manejar el modal de confirmación
  const handleOpenConfirmationModal = () => {
    setShowConfirmationModal(true);
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const handleConfirmInvoice = async () => {
    setShowConfirmationModal(false);
    // Ejecutar el submit del formulario
    formik.handleSubmit();
  };

  // Funciones para manejar el modal de éxito
  const handleNewInvoice = () => {
    setShowSuccessModal(false);
    // El formulario ya está limpio, solo necesitamos mostrarlo
  };

  const handleGoToList = () => {
    setShowSuccessModal(false);
    navigate('/billing-list');
  };

  // Estados locales para los catálogos
  const [documentTypes, setDocumentTypes] = useState<any[]>([]);
  const [paymentMethodsList, setPaymentMethodsList] = useState<any[]>([]);
  const [invoiceTypes, setInvoiceTypes] = useState<any[]>([]);
  const [taxTypes, setTaxTypes] = useState<any[]>([]);

  // Refs para controlar el flujo de carga
  const catalogsLoaded = useRef(false);
  const docTypesLoaded = useRef(false);
  const paymentMethodsLoaded = useRef(false);
  const invoiceTypesLoaded = useRef(false);
  const taxTypesLoaded = useRef(false);

  const paymentMethodCatalogId = useRef<string | null>(null);
  const invoiceTypeCatalogId = useRef<string | null>(null);
  const taxTypeCatalogId = useRef<string | null>(null);

  useEffect(() => {
    dispatch(getCatalogs());
  }, [dispatch]);

  useEffect(() => {
    if (selectedCompany) {
      dispatch(getCustomers({ companyId: selectedCompany.id, isCustomer: true }));
    }
  }, [dispatch, selectedCompany]);

  // 🔄 Forzar carga inicial de clientes cuando hay compañía
  useEffect(() => {
    if (selectedCompany && customerList.length === 0) {
      dispatch(getCustomers({ companyId: selectedCompany.id, isCustomer: true }));
    }
  }, [selectedCompany, customerList.length, dispatch]);

  // Lógica para cargar catálogos usando descendants por ID
  useEffect(() => {
    if (Array.isArray(catalogList) && catalogList.length > 0 && !catalogsLoaded.current) {
      catalogsLoaded.current = true;

      const idCatalog = catalogList.find((c: any) => c.name === 'Tipos de identificación');
      const pmCatalog = catalogList.find((c: any) => c.name === 'Formas de Pago');
      const itCatalog = catalogList.find((c: any) => c.name === 'Tipos de comprobantes');
      const tCatalog = catalogList.find((c: any) => c.name === 'Tipos de impuestos');

      if (idCatalog) {
        paymentMethodCatalogId.current = pmCatalog?.id || null;
        invoiceTypeCatalogId.current = itCatalog?.id || null;
        taxTypeCatalogId.current = tCatalog?.id || null;

        dispatch(getDescendants(idCatalog.id));
      }
    }
  }, [catalogList, dispatch]);

  // Cargar Tipos de Identificación
  useEffect(() => {
    if (catalogsLoaded.current && !docTypesLoaded.current) {
      const idCatalog = catalogList.find((c: any) => c.name === 'Tipos de identificación');
      if (idCatalog && descendants[idCatalog.id]) {
        setDocumentTypes(descendants[idCatalog.id]);
        docTypesLoaded.current = true;

        if (paymentMethodCatalogId.current) {
          dispatch(getDescendants(paymentMethodCatalogId.current));
        }
      }
    }
  }, [descendants, catalogList, dispatch]);

  // Cargar Formas de Pago
  useEffect(() => {
    if (docTypesLoaded.current && !paymentMethodsLoaded.current) {
      if (paymentMethodCatalogId.current && descendants[paymentMethodCatalogId.current]) {
        setPaymentMethodsList(descendants[paymentMethodCatalogId.current]);
        paymentMethodsLoaded.current = true;

        if (invoiceTypeCatalogId.current) {
          dispatch(getDescendants(invoiceTypeCatalogId.current));
        }
      }
    }
  }, [descendants, dispatch]);

  // Cargar Tipos de Comprobantes
  useEffect(() => {
    if (paymentMethodsLoaded.current && !invoiceTypesLoaded.current) {
      if (invoiceTypeCatalogId.current && descendants[invoiceTypeCatalogId.current]) {
        setInvoiceTypes(descendants[invoiceTypeCatalogId.current]);
        invoiceTypesLoaded.current = true;

        if (taxTypeCatalogId.current) {
          dispatch(getDescendants(taxTypeCatalogId.current));
        }
      }
    }
  }, [descendants, dispatch]);

  // Cargar Tipos de Impuestos
  useEffect(() => {
    if (invoiceTypesLoaded.current && !taxTypesLoaded.current) {
      if (taxTypeCatalogId.current && descendants[taxTypeCatalogId.current]) {
        setTaxTypes(descendants[taxTypeCatalogId.current]);
        taxTypesLoaded.current = true;
      }
    }
  }, [descendants, dispatch]);


  useEffect(() => {
    if (companies && companies.length > 0 && !selectedCompany) {
      if (isSuperAdmin && decodedToken?.companyId) {
        const tokenCompany = companies.find((c: any) => c.id === decodedToken.companyId);
        if (tokenCompany) {
          setSelectedCompany(tokenCompany);
          return;
        }
      }
      setSelectedCompany(companies[0]);
    }
  }, [companies, selectedCompany, isSuperAdmin, decodedToken]);

  useEffect(() => {
    if (!taxTypes || taxTypes.length === 0) return;
    setDetails(prevDetails => {
      if (!prevDetails || prevDetails.length === 0) return prevDetails;
      let changed = false;
      const updated = prevDetails.map(d => {
        let u = { ...d };

        if (!u.selectedTaxId) {
          const ivaTax = taxTypes.find((t: any) => t.name === 'IVA') || taxTypes[0];
          const rate15 = ivaTax?.children?.find((r: any) => r.jsonData?.rate === 15);
          const fallbackRate = ivaTax?.children && ivaTax.children[0];
          const selRate = rate15 || fallbackRate;
          if (ivaTax && selRate) {
            u.selectedTaxId = ivaTax.id;
            u.selectedRateId = selRate.id;
            u.taxes = [{ code: ivaTax.id, percentageCode: selRate.id, rate: selRate.jsonData?.rate || 0 }];
            changed = true;
          }
        } else if (u.selectedTaxId && !u.selectedRateId) {
          // Si tiene impuesto pero no tarifa, elegir la primera disponible
          const tax = taxTypes.find((t: any) => t.id === u.selectedTaxId);
          const firstRate = tax?.children && tax.children[0];
          if (tax && firstRate) {
            u.selectedRateId = firstRate.id;
            u.taxes = [{ code: tax.id, percentageCode: firstRate.id, rate: firstRate.jsonData?.rate || 0 }];
            changed = true;
          }
        }
        return u;
      });
      return changed ? updated : prevDetails;
    });
  }, [taxTypes]);

  useEffect(() => {
    if (establishments && establishments.length > 0) {
      const defaultEstablishment = establishments.find((e: any) => e.code === '001') || establishments[0];
      if (selectedEstablishment?.id !== defaultEstablishment.id) {
        setSelectedEstablishment(defaultEstablishment);
      }
    } else if ((!establishments || establishments.length === 0) && selectedEstablishment) {
      setSelectedEstablishment(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [establishments, selectedEstablishment]);

  useEffect(() => {
    if (emissionPoints && emissionPoints.length > 0) {
      const defaultEmissionPoint = emissionPoints.find((e: any) => e.code === '001') || emissionPoints[0];
      if (selectedEmissionPoint?.id !== defaultEmissionPoint.id) {
        setSelectedEmissionPoint(defaultEmissionPoint);
      }
    } else if ((!emissionPoints || emissionPoints.length === 0) && selectedEmissionPoint) {
      setSelectedEmissionPoint(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emissionPoints, selectedEmissionPoint]);

  const validationSchema = Yup.object({
    docCode: Yup.string().required("Tipo de comprobante es requerido"),
    buyerIdType: Yup.string().required("Tipo de identificación es requerido"),
    buyerId: Yup.string().required("Identificación es requerida"),
    buyerName: Yup.string()
      .required("Razón Social / Nombres es requerido")
      .matches(/^[^<>&"']*$/, "No se permiten caracteres especiales (<, >, &, \", ')"),
    buyerAddress: Yup.string()
      .matches(/^[^<>&"']*$/, "No se permiten caracteres especiales (<, >, &, \", ')"),
    buyerPhone: Yup.string()
      .matches(/^[^<>&"']*$/, "No se permiten caracteres especiales (<, >, &, \", ')"),
    buyerEmail: Yup.string()
      .email("Email no válido")
      .matches(/^[^<>&"']*$/, "No se permiten caracteres especiales (<, >, &, \", ')"),
    issueDate: Yup.date().required("Fecha de emisión es requerida"),
  });

  // Totals Calculation
  const calculateSubtotal = () => {
    return details.reduce((acc, item) =>
      new Decimal(acc).plus(new Decimal(item.quantity).times(item.unitPrice)).toNumber()
      , 0);
  };

  const calculateDiscount = () => {
    return details.reduce((acc, item) =>
      new Decimal(acc).plus(item.discount).toNumber()
      , 0);
  };

  const calculateTax = () => {
    return details.reduce((acc, item) => {
      const subtotal = new Decimal(item.quantity).times(item.unitPrice).minus(item.discount);
      const rate = item.taxes[0]?.rate || 0;
      const taxVal = subtotal.times(rate).dividedBy(100);
      return new Decimal(acc).plus(taxVal).toNumber();
    }, 0);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: "",
      docCode: "",
      emissionType: "1",
      buyerIdType: "",
      buyerName: "",
      buyerId: "",
      buyerAddress: "",
      buyerPhone: "",
      buyerEmail: "",
      issueDate: new Date().toISOString().split('T')[0],
      remissionGuide: "",
      negotiableCommercialInvoice: false,
      tip: 0.0,
      currency: "DOLAR",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setShowDetailsErrors(true);
      setIsSubmitting(true);

      if (paymentMethods.some(p => !p.methodId)) {
        toast.error("Por favor, seleccione una forma de pago válida para todos los registros.");
        setIsSubmitting(false);
        return;
      }

      if (details.some(d => !d.mainCode || !d.description)) {
        setIsSubmitting(false);
        return;
      }

      const sanitizeXMLString = (str: string | undefined | null) => {
        if (!str) return "";
        return str.replace(/[<>&"']/g, '');
      };

      try {

        let finalCustomerId = null; // Inicializar como null para detectar problemas


        if (selectedCompany || isSuperAdmin) {

          // 🔄 Asegurar que siempre haya un companyId válido
          let companyId = selectedCompany?.id;

          // Para Super Admin, usar el companyId del token si no hay compañía seleccionada
          if (!companyId && isSuperAdmin && decodedToken?.companyId) {
            companyId = decodedToken.companyId;
          }

          // Validación final
          if (!companyId) {
            toast.error('No se pudo determinar la compañía para generar la factura');
            setIsSubmitting(false);
            return;
          }

          const customerPayload = {
            companyId: companyId,
            identityDocumentTypeId: values.buyerIdType,
            identificationNumber: values.buyerId,
            name: sanitizeXMLString(values.buyerName),
            email: sanitizeXMLString(values.buyerEmail),
            address: sanitizeXMLString(values.buyerAddress),
            phone: sanitizeXMLString(values.buyerPhone),
            isCustomer: true
          };

          // 🔍 BÚSQUEDA ROBUSTA: Por companyId + identificationNumber
          const existingCustomer = customerList.find(
            (c: any) =>
              c.identificationNumber === values.buyerId &&
              (isSuperAdmin ? true : c.companyId === selectedCompany?.id)  // ✅ Super Admin busca global, Admin filtra por compañía
          );


          if (existingCustomer) {
            // 🔄 ACTUALIZAR CLIENTE EXISTENTE

            try {
              const updateResponse = await dispatch(
                updateCustomer({ customer: { ...customerPayload, id: existingCustomer.id }, showSuccessToast: false })
              ).unwrap();

              finalCustomerId = updateResponse?.id || updateResponse?.data?.id || existingCustomer.id;

            } catch (updateError) {
              finalCustomerId = existingCustomer.id;
            }

          } else {

            try {
              await dispatch(
                addCustomer(customerPayload)
              ).unwrap();

              // 2. Esperar un momento para asegurar que se guarde en BD
              await new Promise(resolve => setTimeout(resolve, 1000));

              // 3. Refrescar lista para obtener datos actualizados
              const refreshResponse = await dispatch(getCustomers({
                companyId: companyId,
                isCustomer: true
              })).unwrap();

              // 4. Buscar el cliente recién creado por identificación
              const createdCustomer = refreshResponse.data?.find(
                (c: any) => c.identificationNumber === customerPayload.identificationNumber
              );

              if (!createdCustomer || !createdCustomer.id) {
                toast.error('No se pudo encontrar el cliente recién creado en la base de datos');
                return;
              }

              finalCustomerId = createdCustomer.id;

            } catch (createError) {
              const errorMessage = createError instanceof Error ? createError.message : String(createError);
              toast.error('No se pudo crear el cliente: ' + errorMessage);
              return;
            }
          }
        } else {
        }

        if (!finalCustomerId) {
          toast.error('No se pudo determinar el ID del cliente para la factura');
          return;
        }


        const invoicePayload = {
          taxInfo: {
            docCode: values.docCode,
            emissionType: values.emissionType,
          },
          invoiceInfo: {
            subjectId: finalCustomerId,
            buyerIdType: values.buyerIdType,
            buyerName: sanitizeXMLString(values.buyerName),
            buyerId: values.buyerId,
            buyerAddress: sanitizeXMLString(values.buyerAddress),
            buyerPhone: sanitizeXMLString(values.buyerPhone),
            buyerEmail: sanitizeXMLString(values.buyerEmail),
            tip: Number(values.tip) || 0,
            currency: values.currency,
          },
          details: details.map(d => ({
            mainCode: sanitizeXMLString(d.mainCode),
            auxiliaryCode: sanitizeXMLString(d.auxiliaryCode),
            description: sanitizeXMLString(d.description),
            quantity: d.quantity,
            unitPrice: d.unitPrice,
            discount: d.discount,
            taxes: [
              {
                code: d.selectedTaxId,
                percentageCode: d.selectedRateId
              }
            ],
          })),
          payments: paymentMethods.map(p => ({
            methodId: p.methodId,
            total: Number(p.total),
            timeUnit: p.timeUnit.toLowerCase(),
            term: Number(p.term)
          })),
          additionalInfo: additionalFields
            .filter(f => !f.readOnly && f.name.trim() !== '-')
                    .map(f => ({
                        name: sanitizeXMLString(f.name),
                        value: sanitizeXMLString(f.value)
                    }))
            .filter(f => f.name.trim() !== '')
        };

        const response = await dispatch(createInvoice(invoicePayload));
        if (createInvoice.fulfilled.match(response)) {
          const payload = response.payload as any;

          if (payload && payload.success) {
            formik.resetForm();
            setDetails([]);
            setPaymentMethods([]);
            setAdditionalFields([]);

            formik.setFieldValue('issueDate', new Date().toISOString().split('T')[0]);

            // Mostrar modal de éxito
            setShowSuccessModal(true);
          } else {
            toast.error(payload?.message || "Ocurrió un error al generar la factura.");
          }
        } else {
          toast.error("Ocurrió un error al generar la factura.");
        }
      } catch (error) {
        console.error("Error al facturar:", error);
        toast.error("Ocurrió un error al generar la factura.");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const phone = formik.values.buyerPhone;
    const email = formik.values.buyerEmail;

    setAdditionalFields(currentFields => {
      const manualFields = currentFields.filter(f => !f.readOnly && f.name.trim() !== '-');
      let updatedFields = [...manualFields];

      const EMAIL_ID = 'email-auto';
      const PHONE_ID = 'phone-auto';

      updatedFields = updatedFields.filter(f => f.id !== EMAIL_ID && f.id !== PHONE_ID);

      if (email && email.trim()) {
        updatedFields.push({
          id: EMAIL_ID,
          name: 'Email',
          value: email.trim(),
          readOnly: true
        });
      }

      if (phone && phone.trim()) {
        updatedFields.push({
          id: PHONE_ID,
          name: 'Teléfono',
          value: phone.trim(),
          readOnly: true
        });
      }

      return updatedFields;
    });
  }, [formik.values.buyerPhone, formik.values.buyerEmail]);

  const handleBuyerIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const id = e.target.value;
    formik.setFieldValue("buyerId", id);
  };

  useEffect(() => {
    if (invoiceTypes && invoiceTypes.length > 0 && !formik.values.docCode) {
      const factura = invoiceTypes.find((t: any) => t.name.toUpperCase().includes('FACTURA'));
      if (factura) {
        formik.setFieldValue('docCode', factura.id);
      } else {
        formik.setFieldValue('docCode', invoiceTypes[0].id);
      }
    }
  }, [invoiceTypes, formik]);

  useEffect(() => {
    const copiedInvoiceData = sessionStorage.getItem('copiedInvoiceData');
    if (copiedInvoiceData && taxTypes.length > 0 && documentTypes.length > 0 && paymentMethodsList.length > 0) {
      try {
        const data = JSON.parse(copiedInvoiceData);

        sessionStorage.removeItem('copiedInvoiceData');

        if (data.invoiceInfo) {
          const info = data.invoiceInfo;
          const docType = documentTypes.find((dt: any) => dt.jsonData?.code === info.tipoIdentificacionComprador);
          formik.setFieldValue('buyerIdType', docType?.id || '');
          formik.setFieldValue('buyerId', info.identificacionComprador || '');
          formik.setFieldValue('buyerName', info.razonSocialComprador || '');
          formik.setFieldValue('buyerAddress', info.direccionComprador || '');
          // Buscar teléfono y email en infoAdicional
          const phoneField = data.additionalInfo?.find((field: any) => field.nombre === 'Telefono');
          const emailField = data.additionalInfo?.find((field: any) => field.nombre === 'Email');
          formik.setFieldValue('buyerPhone', phoneField?.valor || '');
          formik.setFieldValue('buyerEmail', emailField?.valor || '');
          const currentDate = new Date().toISOString().split('T')[0];
          formik.setFieldValue('issueDate', currentDate);
          formik.setFieldValue('remissionGuide', info.guiaRemision || '');
          formik.setFieldValue('tip', parseFloat(info.propina) || 0);
          formik.setFieldValue('currency', info.moneda || 'DOLAR');
        }

        if (data.details && Array.isArray(data.details)) {
          const loadedDetails = data.details.map((detail: any) => {
            let selectedTaxId = "";
            let selectedRateId = "";
            let taxRate = 0;

            if (detail.impuestos && Array.isArray(detail.impuestos)) {
              const tax = detail.impuestos[0];
              if (tax) {
                const taxType = taxTypes.find((t: any) => t.jsonData?.code === tax.codigo);

                if (taxType) {
                  selectedTaxId = taxType.id;
                  const rateByValue = taxType.children.find((r: any) => r.jsonData?.rate === parseFloat(tax.tarifa));

                  if (rateByValue) {
                    selectedRateId = rateByValue.id;
                    taxRate = rateByValue.jsonData?.rate || 0;
                  } else {
                    const rate = taxType.children.find((r: any) => r.jsonData?.code === tax.codigoPorcentaje);

                    if (rate) {
                      selectedRateId = rate.id;
                      taxRate = rate.jsonData?.rate || 0;
                    } else {
                      const zeroRate = taxType.children.find((r: any) => r.jsonData?.rate === 0);
                      if (zeroRate) {
                        selectedRateId = zeroRate.id;
                        taxRate = 0;
                      } else {
                        const firstRate = taxType.children[0];
                        selectedRateId = firstRate?.id || '';
                        taxRate = firstRate?.jsonData?.rate || 0;
                      }
                    }
                  }
                } else {
                  const ivaTax = taxTypes.find((t: any) => t.name === 'IVA');

                  if (ivaTax) {
                    selectedTaxId = ivaTax.id;
                    const firstRate = ivaTax.children[0];
                    selectedRateId = firstRate?.id || '';
                    taxRate = firstRate?.jsonData?.rate || 0;
                  }
                }
              }
            } else {
              const ivaTax = taxTypes.find((t: any) => t.name === 'IVA');
              if (ivaTax) {
                selectedTaxId = ivaTax.id;
                const defaultRate = ivaTax.children.find((r: any) => r.jsonData.rate === 15) || ivaTax.children[0];
                selectedRateId = defaultRate?.id || '';
                taxRate = defaultRate?.jsonData?.rate || 0;
              }
            }

            return {
              id: Math.random().toString(36).slice(2, 9),
              mainCode: detail.codigoPrincipal || '',
              auxiliaryCode: detail.codigoAuxiliar || '',
              description: detail.descripcion || '',
              quantity: parseFloat(detail.cantidad) || 1,
              unitPrice: parseFloat(detail.precioUnitario) || 0,
              discount: parseFloat(detail.descuento) || 0,
              selectedTaxId,
              selectedRateId,
              taxes: [{
                code: selectedTaxId,
                percentageCode: selectedRateId,
                rate: taxRate
              }]
            };
          });

          setDetails(loadedDetails);
        }

        // Cargar formas de pago
        if (data.payments && Array.isArray(data.payments)) {
          const loadedPayments = data.payments.map((payment: any) => {
            // Mapear forma de pago
            let methodId = "";
            if (paymentMethodsList && paymentMethodsList.length > 0) {
              const method = paymentMethodsList.find((pm: any) => pm.jsonData?.code === payment.formaPago);
              methodId = method ? method.id : paymentMethodsList[0].id;
            }

            return {
              id: Math.random().toString(36).slice(2, 9),
              methodId,
              total: parseFloat(payment.total) || 0,
              timeUnit: payment.plazo ? 'dias' : 'dias',
              term: parseInt(payment.plazo) || 0
            };
          });

          setPaymentMethods(loadedPayments);
        }

        if (paymentMethodsList && paymentMethodsList.length > 0 && (!data.payments || data.payments.length === 0)) {
          const defaultMethod = paymentMethodsList.find((pm: any) => pm.jsonData.code === '01') || paymentMethodsList[0];
          setPaymentMethods([{
            id: Math.random().toString(36).slice(2, 9),
            methodId: defaultMethod.id,
            total: 0,
            timeUnit: 'dias',
            term: 0
          }]);
        }

        // Cargar campos adicionales (filtrando campos inválidos y duplicados)
        if (data.additionalInfo && Array.isArray(data.additionalInfo)) {
          const loadedFields = data.additionalInfo
            .filter((field: any) => {
              // Excluir campos inválidos
              if (!field.nombre || field.nombre.trim() === '-') return false;

              // Excluir campos que se generarán automáticamente (Email y Teléfono)
              const normalizedName = field.nombre.trim().toLowerCase();
              return normalizedName !== 'email' && normalizedName !== 'teléfono' && normalizedName !== 'telefono';
            })
            .map((field: any) => ({
              id: Math.random().toString(36).slice(2, 9),
              name: field.nombre || '',
              value: field.valor || ''
            }));

          setAdditionalFields(loadedFields);
        }

      } catch (error) {
        console.error('Error al cargar datos copiados:', error);
      } finally {
        // En cualquier caso, ocultar el loader para mostrar el formulario o permitir reintentos
        setLoadingCopy(false);
      }
    }
  }, [taxTypes, paymentMethodsList, documentTypes, formik]);

  useEffect(() => {
    const hasCopied = !!sessionStorage.getItem('copiedInvoiceData');
    if (hasCopied && (taxTypes.length === 0 || documentTypes.length === 0 || paymentMethodsList.length === 0)) {
      setLoadingCopy(true);
    }
  }, [taxTypes.length, documentTypes.length, paymentMethodsList.length]);

  const addProductToDetails = (product: any) => {
    // Buscar IVA por defecto (15% o 12%)
    let defaultTaxId = "";
    let defaultRateId = "";
    let defaultRate = 0;

    const ivaTax = taxTypes.find((t: any) => t.name === "IVA");
    if (ivaTax) {
      defaultTaxId = ivaTax.id;
      // Buscar 15% por defecto, si no 12%
      const rate15 = ivaTax.children.find((r: any) => r.jsonData.rate === 15);
      const rate12 = ivaTax.children.find((r: any) => r.jsonData.rate === 12);
      const selectedRate = rate15 || rate12 || ivaTax.children[0];

      if (selectedRate) {
        defaultRateId = selectedRate.id;
        defaultRate = selectedRate.jsonData.rate;
      }
    }

    const newDetail: InvoiceDetail = {
      id: Math.random().toString(36).slice(2, 9),
      mainCode: product.code || "",
      description: product.name || "",
      auxiliaryCode: product.auxiliaryCode || "",
      quantity: 1,
      unitPrice: product.price || 0,
      discount: 0,
      selectedTaxId: defaultTaxId,
      selectedRateId: defaultRateId,
      taxes: [
        {
          code: defaultTaxId,
          percentageCode: defaultRateId,
          rate: defaultRate
        },
      ],
    };
    setDetails([...details, newDetail]);
  };

  const addEmptyDetail = () => {
    // Buscar IVA por defecto
    let defaultTaxId = "";
    let defaultRateId = "";
    let defaultRate = 0;

    const ivaTax = taxTypes.find((t: any) => t.name === "IVA");
    if (ivaTax) {
      defaultTaxId = ivaTax.id;
      const selectedRate = ivaTax.children[0];

      if (selectedRate) {
        defaultRateId = selectedRate.id;
        defaultRate = selectedRate.jsonData.rate;
      }
    }

    const newDetail: InvoiceDetail = {
      id: Math.random().toString(36).slice(2, 9),
      mainCode: "",
      description: "",
      auxiliaryCode: "",
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      selectedTaxId: defaultTaxId,
      selectedRateId: defaultRateId,
      taxes: [
        {
          code: defaultTaxId,
          percentageCode: defaultRateId,
          rate: defaultRate
        },
      ],
    };
    setDetails([...details, newDetail]);
  };

  const removeDetail = (id: string) => {
    setDetails(details.filter((d) => d.id !== id));
  };

  const updateDetail = (id: string, field: keyof InvoiceDetail, value: any) => {
    setDetails(
      details.map((d) => {
        if (d.id !== id) return d;

        const updatedDetail = { ...d, [field]: value };

        if (field === 'selectedTaxId' || field === 'selectedRateId') {
          const taxId = field === 'selectedTaxId' ? value : d.selectedTaxId;
          const rateId = field === 'selectedRateId' ? value : d.selectedRateId;

          const taxType = taxTypes.find((t: any) => t.id === taxId);
          const rateObj = taxType?.children.find((r: any) => r.id === rateId);
          const rate = rateObj ? rateObj.jsonData.rate : 0;

          updatedDetail.taxes = [{
            code: taxId,
            percentageCode: rateId,
            rate: rate
          }];
          if (field === 'selectedTaxId') updatedDetail.selectedRateId = rateObj ? rateObj.id : '';
        }
        return updatedDetail;
      })
    );
  };

  const addPaymentMethod = () => {
    if (!paymentMethodsList || paymentMethodsList.length === 0) {
      toast.warning("Las formas de pago aún no se han cargado. Espere un momento.");
      return;
    }

    const defaultMethod = paymentMethodsList.find((pm: any) => pm.jsonData.code === '01') || paymentMethodsList[0];

    const newPaymentMethod: PaymentMethod = {
      id: Math.random().toString(36).slice(2, 9),
      methodId: defaultMethod ? defaultMethod.id : "",
      total: paymentMethods.length === 0 ? calculateTotal() : 0,
      timeUnit: "dias",
      term: 0
    };
    setPaymentMethods([...paymentMethods, newPaymentMethod]);
  };

  const removePaymentMethod = (id: string) => {
    setPaymentMethods(paymentMethods.filter(p => p.id !== id));
  };

  const updatePaymentMethod = (id: string, field: keyof PaymentMethod, value: any) => {
    setPaymentMethods(paymentMethods.map(p => (p.id === id ? { ...p, [field]: value } : p)));
  };

  // Additional Fields Logic
  const addAdditionalField = () => {
    const newField: AdditionalField = {
      id: Math.random().toString(36).slice(2, 9),
      name: "",
      value: ""
    };
    setAdditionalFields([...additionalFields, newField]);
  };

  const removeAdditionalField = (id: string) => {
    setAdditionalFields(additionalFields.filter(f => f.id !== id));
  };

  const updateAdditionalField = (id: string, field: keyof AdditionalField, value: any) => {
    setAdditionalFields(additionalFields.map(f => (f.id === id ? { ...f, [field]: value } : f)));
  };

  const calculateTaxesBreakdown = () => {
    const breakdown: { [key: string]: { rate: number, base: number, tax: number } } = {};

    details.forEach(item => {
      const subtotal = new Decimal(item.quantity).times(item.unitPrice).minus(item.discount);
      const rate = item.taxes[0]?.rate || 0;
      const taxValue = subtotal.times(rate).dividedBy(100);
      const key = rate.toString();

      if (!breakdown[key]) {
        breakdown[key] = { rate, base: 0, tax: 0 };
      }
      breakdown[key].base = new Decimal(breakdown[key].base).plus(subtotal).toNumber();
      breakdown[key].tax = new Decimal(breakdown[key].tax).plus(taxValue).toNumber();
    });

    return Object.values(breakdown).sort((a, b) => b.rate - a.rate);
  };

  const calculateTotal = () => {
    const subtotal = new Decimal(calculateSubtotal());
    const discount = new Decimal(calculateDiscount());
    const tax = new Decimal(calculateTax());
    const tip = new Decimal(formik.values.tip || 0);

    const total = subtotal.minus(discount).plus(tax).plus(tip);
    return total.toDecimalPlaces(2).toNumber();
  };

  const calculatePaymentMethodsTotal = () => {
    return paymentMethods.reduce((acc, item) => acc + Number(item.total || 0), 0);
  };

  return (
    <div className="page-content position-relative">
      <Container fluid>
        {loadingCopy && (
          <FullScreenLoader
            title="Preparando documento"
            message="Estamos configurando los detalles y catálogos necesarios para duplicar su factura. Esto tomará solo un momento."
            iconType="copy"
          />
        )}

        {isSubmitting && (
          <FullScreenLoader
            title="Procesando Factura"
            message="Estamos generando y enviando su factura al SRI. Esto tomará solo un momento."
            showProgress={true}
            iconType="printer"
          />
        )}

        {!loadingCopy && !isSubmitting && (
          <>
            <BreadCrumb title="Nueva Facturar" pageTitle="Inicio" />
            <Row className="justify-content-center">
              <Col lg={12}>
                <Card className="shadow-lg border-0 rounded-3">
                  <BillingHeader
                    invoiceTypes={invoiceTypes}
                    value={formik.values.docCode}
                    onChange={formik.handleChange}
                  />
                  <CardBody className="p-4 bg-light">
                    <Form>
                      {/* Company Section */}
                      <IssuerInfo
                        formik={formik}
                        selectedCompany={selectedCompany}
                        selectedEstablishment={selectedEstablishment}
                        selectedEmissionPoint={selectedEmissionPoint}
                        onCompanyChange={setSelectedCompany}
                      />

                      {/* Customer Section */}
                      <CustomerInfo
                        formik={formik}
                        documentTypes={documentTypes}
                        handleBuyerIdChange={handleBuyerIdChange}
                        selectedCompanyId={selectedCompany?.id}
                      />

                      {/* Details Section */}
                      <InvoiceDetails
                        details={details}
                        taxTypes={taxTypes}
                        onAddProduct={addProductToDetails}
                        onAddEmpty={addEmptyDetail}
                        onUpdateDetail={updateDetail}
                        onRemoveDetail={removeDetail}
                        showValidationErrors={showDetailsErrors}
                      />

                      <Row>
                        <Col lg={6}>
                          {/* Payment Methods */}
                          <PaymentMethods
                            paymentMethods={paymentMethods}
                            paymentMethodsList={paymentMethodsList}
                            onAdd={addPaymentMethod}
                            onRemove={removePaymentMethod}
                            onUpdate={updatePaymentMethod}
                            totalToPay={calculateTotal()}
                            paymentsTotal={calculatePaymentMethodsTotal()}
                          />

                          {/* Additional Fields */}
                          <AdditionalFields
                            fields={additionalFields}
                            onAdd={addAdditionalField}
                            onRemove={removeAdditionalField}
                            onUpdate={updateAdditionalField}
                          />
                        </Col>

                        <Col lg={6}>
                          <TotalsSummary
                            formik={formik}
                            calculateSubtotal={calculateSubtotal}
                            calculateDiscount={calculateDiscount}
                            calculateTax={calculateTax}
                            calculateTotal={calculateTotal}
                            taxesBreakdown={calculateTaxesBreakdown()}
                          />
                        </Col>
                      </Row>

                      <div className="d-flex justify-content-end pt-3 border-top">
                        <Button
                          color="success"
                          type="button"
                          className="px-4 fw-bold shadow-sm"
                          onClick={handleOpenConfirmationModal}
                          disabled={
                            Math.abs(calculateTotal() - calculatePaymentMethodsTotal()) > 0.009 ||
                            calculateTotal() === 0 ||
                            isSubmitting
                          }
                        >
                          {isSubmitting ? (
                            <>
                              <Spinner size="sm" className="me-2"> Loading... </Spinner>
                              <span>Enviando...</span>
                            </>
                          ) : (
                            <>
                              <i className="ri-save-3-line me-1"></i> Generar Factura
                            </>
                          )}
                        </Button>
                      </div>
                    </Form>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Container>

      <GenericSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onPrimaryAction={handleNewInvoice}
        onSecondaryAction={handleGoToList}
        config={createBillingSuccessConfig()}
      />

      <ConfigValidationModal
        isOpen={showConfigModal}
        isAdmin={isAdmin}
      />

      <ConfirmModal
        isOpen={showConfirmationModal}
        onClose={handleCloseConfirmationModal}
        onConfirm={handleConfirmInvoice}
        data={createBillingConfirmData({
          customerName: formik.values.buyerName,
          customerId: formik.values.buyerId,
          subtotal: calculateSubtotal() - calculateDiscount(),
          totalTax: calculateTax(),
          tip: formik.values.tip,
          totalAmount: calculateTotal()
        })}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default Billing;
